import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ContentChild, ContentChildren, Input, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatColumnDef, MatNoDataRow, MatTable, MatTableDataSource } from '@angular/material/table';
import { TableVirtualScrollDataSource } from 'app/shared/modules/table/table-virtual-scroll';
import { TableConfig } from '../../model/table.config';

/**
 * TableComponent
 *
 * Reusable table component with some implemented feature like sticky headers, expandable rows, ...
 *
 */
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TableComponent<T> implements AfterViewInit {
  /**
   * Table Configuration Input
   */
  @Input() set config(config: TableConfig) {
    this.tableConfig = config;
  }

  /**
   * Table Data Input
   */
  @Input() set data(data: T[]) {
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource<T>();
    }
    (this.dataSource.data as T[]) = data;
  }

  /**
   * source of the table data
   */
  @Input() dataSource: MatTableDataSource<T> | TableVirtualScrollDataSource<T>;

  /**
   * Optional detail template for the expanded view. This works only, when row configuration is set to 'expandable: true'
   */
  @Input() detailTemplate: TemplateRef<T>;

  /**
   * Material Sort Input. Sorting of columns need a MatSort instance, which needs to be passed to this component like:
   *
   * @example
   * <app-table [config]="tableConfig" [data]="data" [sort]="sort" matSort #sort="matSort"></app-table>
   */
  @Input() sort: MatSort;

  /**
   * Is loading indicator to show loading spinner on initial loading
   */
  @Input() isLoading: boolean;

  /**
   * rendered table instance
   */
  @ViewChild(MatTable, { static: true }) table: MatTable<T>;

  /**
   * no data row template variable to set the no data row to the table
   */
  @ContentChild(MatNoDataRow) noDataRow: MatNoDataRow;

  /**
   * column definitions to set the missing row in the table (needs to be done because of the ng-content)
   */
  @ContentChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;

  /**
   * string array to show columns
   */
  @Input() set displayedColumns(displayColumns: string[]) {
    if (this.columnDefs && this.columnDefs.length > 0) {
      this.tableDisplayColumns = displayColumns;
    } else {
      this.tableDisplayColumnsCache = displayColumns;
    }
  }

  public tableDisplayColumns: string[];
  private tableDisplayColumnsCache: string[];
  /**
   * table configuration
   */
  public tableConfig: TableConfig;

  /**
   * source of the table data
   */
  public expandedElement: any;

  /**
   * Toggles the row expand
   *
   * @param row data of the row
   */
  public toggleRow(row: any): void {
    this.expandedElement = this.expandedElement && row && this.expandedElement.Id === row.Id ? null : row;
  }

  /**
   * Needed to avoid blinking of spinner after update.
   */
  public trackElement(index: number, element: any) {
    return element ? element.Id : index;
  }

  ngAfterViewInit() {
    // add missing column definitions
    const displayColumns = [];

    this.columnDefs.forEach((columnDef) => {
      this.table.addColumnDef(columnDef);
      displayColumns.push(columnDef.name);
    });

    /**
     * DEVELOPER NOTE
     *
     * I don't know, why the display columns are not yet loaded in the table,
     * but we have to wait until the table is finished with column loading. Otherwise there will be a
     * "Unknown Column" error
     */
    setTimeout(() => {
      if (this.columnDefs && this.columnDefs.length > 0 && this.tableDisplayColumnsCache) {
        this.tableDisplayColumns = this.tableDisplayColumnsCache;
      } else {
        this.tableDisplayColumns = displayColumns;
      }
    }, 0);

    // set the no data row
    this.table.setNoDataRow(this.noDataRow);

    // set the sorting
    this.dataSource.sort = this.sort;
  }
}
